import { FullScreenLoading, Layout, Loader, NavigationFeedback } from '@ui'
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import ProtectedRoute from './PrtotectedRoute'

// Constants
import { APP_ROUTES_UNAUTHORIZE } from '@data'
import { useProfile as useProfileHook } from '@hook'
import { useAppSelector } from '@store/hooks'
import { useProfile } from '@zustore/store'
import { lazy, Suspense, useEffect, useMemo } from 'react'
import PatientLayout from '../components/common/Layout/PatientLayout'
import { isPermissionAllowedByModules } from '../modules/access'
import { ModuleThemeProvider } from '../modules/ModuleThemeProvider'
import { useProductModules } from '../modules/useProductModules'
import { FacilityAttributes } from '../types/facility.type'
import { appRoutes as allAppRoutes } from './app.routes'
import { patientRoutes } from './patient.routes'
import { publicRoutes } from './public.routes'

const Unauthorized = lazy(() => import('../pages/Unauthorized'))
const DoctorDashboard = lazy(() => import('@doctor/Home'))
const NurseDashboard = lazy(() => import('../pages/Nurse/Home'))
const PharmacistDashboard = lazy(() => import('../pages/Pharmacist/Home'))
const LabDashboard = lazy(() => import('@pages/LabTech'))
const RadiologistDashboard = lazy(() => import('@pages/Radiologist'))
const PatientDashboard = lazy(() => import('@pages/Patient'))
const CustomDashboard = lazy(() => import('../pages/Dashboard/index'))

const IndexDashboard = () => {
  const { user } = useAppSelector(state => state.user)
  const isPatient = user?.authable_type?.toLowerCase() === 'patient'
  // Staff /profile is not needed for patient portal — fetching it delays mount and can remount routes.
  const { profile, profileIsLoading } = useProfileHook({ isLoggedIn: !!user?.authable_id && !isPatient })
  const userRoles = profile?.meta?.roles || []
  const { enabledModules, isModuleEnabled } = useProductModules()

  if (!isPatient && profileIsLoading) return <FullScreenLoading />

  const clinicalMods = enabledModules.filter(m => !['core', 'patient_portal'].includes(m))

  let dashboard = <CustomDashboard />
  if (isPatient) {
    dashboard = <PatientDashboard />
  } else if (clinicalMods.length === 1 && clinicalMods[0] === 'laboratory') {
    dashboard = <LabDashboard />
  } else if (clinicalMods.length === 1 && clinicalMods[0] === 'radiology') {
    dashboard = <RadiologistDashboard />
  } else if (clinicalMods.length === 1 && clinicalMods[0] === 'pharmacy') {
    dashboard = <PharmacistDashboard />
  } else if (isModuleEnabled('clinical') && userRoles.includes('doctor')) {
    dashboard = <DoctorDashboard />
  } else if (isModuleEnabled('clinical') && userRoles.includes('nurse')) {
    dashboard = <NurseDashboard />
  } else if (isModuleEnabled('pharmacy') && userRoles.includes('pharmacist')) {
    dashboard = <PharmacistDashboard />
  } else if (isModuleEnabled('laboratory') && userRoles.includes('laboratory_technician')) {
    dashboard = <LabDashboard />
  } else if (isModuleEnabled('radiology') && userRoles.includes('radiologist')) {
    dashboard = <RadiologistDashboard />
  }

  return <Suspense fallback={<Loader />}>{dashboard}</Suspense>
}

const AppRoutes = () => {
  const { user } = useAppSelector(state => state.user)
  const isLoggedIn = !!user?.authable_id
  const isPatient = user?.authable_type?.toLowerCase() === 'patient'
  // Do not hit staff /profile for patients — when it resolves, catch-all route swaps and remounts Charts.
  const { profile, profileIsLoading, included } = useProfileHook({ isLoggedIn: isLoggedIn && !isPatient })
  const { setRole, setUser, setFacility } = useProfile()
  const { enabledModules, bypassModuleGates, productSku } = useProductModules()

  // Stable element identity — avoid remounting PatientLayout/Charts on product_modules re-renders.
  const layoutElement = useMemo(() => (isPatient ? <PatientLayout /> : <Layout />), [isPatient])

  const userRoles = useMemo(() => profile?.meta?.roles || [], [profile])

  useEffect(() => {
    if (profile) {
      setRole(profile.meta.roles || [])
      setUser(profile.attributes || null)
      setFacility((included.find(item => item.type === 'facility')?.attributes as FacilityAttributes) || null)
    }
  }, [profile, setRole, setUser, included, setFacility])

  // Patient routes must not recompute when product modules load — that remounts Charts under Suspense.
  const patientAppRoutes = useMemo(() => {
    if (!isLoggedIn || !isPatient) return []
    return patientRoutes
  }, [isLoggedIn, isPatient])

  const staffAppRoutes = useMemo(() => {
    if (!isLoggedIn || isPatient) return []
    // Wait for profile roles before mounting protected app routes so /pharmacy-workspace
    // is not swallowed by the catch-all Unauthorized page during load.
    if (profileIsLoading || !userRoles?.length) return []
    return allAppRoutes
      .filter((r, i, arr) => arr.findIndex(x => x.path === r.path) === i)
      .filter(r => isPermissionAllowedByModules(r.permissionKey, enabledModules, bypassModuleGates, productSku))
  }, [isLoggedIn, isPatient, profileIsLoading, userRoles?.length, enabledModules, bypassModuleGates, productSku])

  const appRoutes = isPatient ? patientAppRoutes : staffAppRoutes

  // Patients: catch-all ready immediately (no profile wait). Staff: wait for roles so * is not Unauthorized early.
  const showAppCatchAll = isLoggedIn && (isPatient || (!profileIsLoading && userRoles.length > 0))

  return (
    <Router future={{ v7_startTransition: true }}>
      <ModuleThemeProvider>
        <NavigationFeedback />
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* PUBLIC ROUTES */}
            {publicRoutes?.map(({ path, element }) => (
              <Route element={element} key={path} path={path} />
            ))}

            <Route element={layoutElement}>
              <Route element={<IndexDashboard />} index />

              {appRoutes?.map(({ path, element, permissionKey }) => {
                const node = permissionKey ? (
                  <ProtectedRoute permissionKey={permissionKey}>{element}</ProtectedRoute>
                ) : (
                  element
                )
                return <Route element={node} key={path} path={path} />
              })}

              <Route
                element={
                  <Suspense fallback={<Loader />}>
                    <Unauthorized />
                  </Suspense>
                }
                path={APP_ROUTES_UNAUTHORIZE}
              />
              {showAppCatchAll ? (
                <Route
                  element={
                    <Suspense fallback={<Loader />}>
                      <Unauthorized />
                    </Suspense>
                  }
                  path="*"
                />
              ) : isLoggedIn ? (
                <Route element={<FullScreenLoading />} path="*" />
              ) : null}
            </Route>
          </Routes>
        </Suspense>
      </ModuleThemeProvider>
    </Router>
  )
}

export default AppRoutes
